/**
 * RoleAssignmentModal — Per-slot assign / invite / remove flow.
 *
 * DEV-1039 iter 3. Rewritten to mirror the ProjectOwnersAdminsPanel
 * "Section + Tray + IdentityCard" pattern:
 *
 *   - Outer modal shell is preserved (parent still opens it per slot).
 *   - Body becomes a calm row list (Pending invitations / Active
 *     assignments) with an "+ Add person" button at the top right.
 *   - Add/Edit slide in via the shared <Tray>.
 *   - Add tray uses the shared <UserIdentityCard> once a person is
 *     picked, surfaces a templated invitation message (live-default,
 *     locks on first edit), and offers two footer actions: direct
 *     Assign and Send invitation. Both code paths preserved.
 *   - Edit tray shows the identity card (no Change button) and an
 *     inline removal confirm with a reason textarea — replacing the
 *     old <ExitReasonDialog> with a single in-tray flow.
 *   - 2FA cross-check: when the slot's bundle requires 2FA AND the
 *     selected user lacks TOTP enrollment, surface the dormant pill +
 *     warning so the admin knows the grant will be inert until the
 *     user enrolls. Mirrors the OA panel's logic.
 *
 * Visibility per row stays on the row (not in tray) — quick toggle for
 * a common operation; not worth a tray round-trip.
 *
 * Existing parent contract preserved end-to-end: same callbacks, same
 * slot shape. New optional props (slotPermissionSet, communityNameByGuid,
 * projectName, currentUserGuid) are degraded-gracefully when omitted, so
 * the parent can adopt them incrementally.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import CloseButton from "../CloseButton/CloseButton";
import { Tray } from "../Tray/Tray";
import {
  UserIdentityCard,
  computeDefaultInviteMessage,
  computeInitials,
  resolveHomeCommunityName,
} from "../IdentityCard/IdentityCard";
import { useApiImpactSignature } from "../../api/useApiImpactSignature";
import { normalizeGuid } from "../../utils/guid";
import type {
  ProjectRoleAssignmentResponse,
  ProjectRoleSlotResponse,
} from "../../api/projectRoles";
import type { SecurityUserResponse } from "../../api/security";
import type { PermissionSetDto } from "../../security/v2/types";
import { mediaSrc } from "../../api/mediaUrl";
import styles from "./RoleAssignmentModal.module.css";

type MemberVisibility = "public" | "team_only" | "anonymous";

interface Props {
  slot: ProjectRoleSlotResponse;
  /** All platform users (active). */
  allUsers: SecurityUserResponse[];
  /** Direct assign — immediately sets status to "active". */
  onAssign: (userGuid: string) => void;
  /** Invite — creates a pending_invitation with optional message. */
  onInvite: (userGuid: string, invitationMessage?: string) => void;
  onRemove: (assignmentGuid: string, reason?: string) => void;
  /** Update the visibility of an existing assignment. Stays on the row. */
  onUpdateVisibility?: (assignmentGuid: string, visibility: MemberVisibility) => void;
  /** Revoke a pending invitation. */
  onRevokeInvitation?: (assignmentGuid: string) => void;
  onClose: () => void;
  /**
   * DEV-1039 iter 3. The permission set bundle granted to this slot (if any).
   * Parent resolves from slotGrants + allBundles. Used for the 2FA cross-check
   * — when bundle.requires2fa is true and the picked user lacks TOTP, the
   * grant will be dormant; we warn the admin before commit. Optional: when
   * undefined or null, the 2FA UI is suppressed (no false positives).
   */
  slotPermissionSet?: PermissionSetDto | null;
  /**
   * DEV-1039 iter 3. Map of community entity GUID (uppercase) → display name.
   * Surfaced by IdentityCard so the admin can confirm the recipient's home
   * community. Optional: when omitted, the card falls back to "no home
   * community" rather than failing.
   */
  communityNameByGuid?: Map<string, string>;
  /**
   * Project name — interpolated into the default invitation message
   * template. When empty, falls back to the localized "this project" string.
   */
  projectName?: string;
  /** Acting user GUID — resolves the sender display name in the templated message. */
  currentUserGuid?: string;
}

function profileImgUrl(user: SecurityUserResponse): string | null {
  return mediaSrc(user.profileImageUrl);
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

type TrayCtx =
  | { mode: "add" }
  | { mode: "edit"; assignment: ProjectRoleAssignmentResponse };

const RoleAssignmentModal: React.FC<Props> = ({
  slot,
  allUsers,
  onAssign,
  onInvite,
  onRemove,
  onUpdateVisibility,
  onRevokeInvitation,
  onClose,
  slotPermissionSet,
  communityNameByGuid,
  projectName,
  currentUserGuid = "",
}) => {
  const { t } = useAppTranslation("projectTeam");

  // ── Tray state ──
  const [trayCtx, setTrayCtx] = useState<TrayCtx | null>(null);

  // Add-mode form state inside the tray
  const [pickUserGuid, setPickUserGuid] = useState<string>("");
  const [userSearch, setUserSearch] = useState("");
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [inviteMessage, setInviteMessage] = useState("");
  /**
   * DEV-1039 iter 3. Live-default invitation message until the admin
   * types — then locks. Reset on tray open and on the "Reset to default"
   * link. Mirrors the OA panel's behavior.
   */
  const [messageUserEdited, setMessageUserEdited] = useState(false);

  /**
   * DEV-1039 iter 3 — Cycle 1 fix. Locally-hidden assignment GUIDs.
   * When the user rescinds/removes, the row disappears from the UI
   * immediately rather than waiting for the parent's refetch to
   * complete. If the backend mutation actually fails, we surface the
   * error in a visible banner so the user knows to retry. This guards
   * against the data-refresh path being slow, cached stale, or silently
   * failing — all of which were possibilities in the cycle 1 blocker.
   */
  const [locallyHiddenGuids, setLocallyHiddenGuids] = useState<Set<string>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);

  // Edit-mode inline removal state
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [removeReason, setRemoveReason] = useState("");

  // ── Derived lookups ──

  const userByGuid = useMemo(() => {
    const m = new Map<string, SecurityUserResponse>();
    allUsers.forEach((u) => m.set(normalizeGuid(u.userGuid), u));
    return m;
  }, [allUsers]);

  /** GUIDs of users currently assigned or invited — excluded from typeahead.
      Filters out locally-hidden GUIDs so re-invite works immediately after
      a rescind, even if the parent's slot data hasn't refreshed yet. */
  const assignedGuids = useMemo(() => {
    const s = new Set<string>();
    (slot.assignments || [])
      .filter((a) =>
        (a.status === "active" ||
          a.status === "pending_approval" ||
          a.status === "pending_invitation" ||
          a.status === "pending_verification" ||
          a.status === "pending_escalated_review") &&
        !locallyHiddenGuids.has(a.assignmentGuid),
      )
      .forEach((a) => s.add(normalizeGuid(a.userGuid)));
    return s;
  }, [slot.assignments, locallyHiddenGuids]);

  /** Typeahead pool — active users not yet assigned/invited. */
  const userOptions = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    const pool = allUsers.filter(
      (u) => !assignedGuids.has(normalizeGuid(u.userGuid)),
    );
    const filtered = q
      ? pool.filter(
          (u) =>
            (u.displayName ?? "").toLowerCase().includes(q) ||
            (u.name ?? "").toLowerCase().includes(q) ||
            (u.email ?? "").toLowerCase().includes(q) ||
            (u.screenName ?? "").toLowerCase().includes(q),
        )
      : pool;
    return filtered
      .slice()
      .sort((a, b) =>
        (a.displayName || a.name || a.email || "").localeCompare(
          b.displayName || b.name || b.email || "",
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
      )
      .slice(0, 50);
  }, [allUsers, userSearch, assignedGuids]);

  const activeAssignments = useMemo(
    () => (slot.assignments || []).filter(
      (a) => a.status === "active" && !locallyHiddenGuids.has(a.assignmentGuid),
    ),
    [slot.assignments, locallyHiddenGuids],
  );

  const pendingInvitations = useMemo(
    () => (slot.assignments || []).filter(
      (a) => a.status === "pending_invitation" && !locallyHiddenGuids.has(a.assignmentGuid),
    ),
    [slot.assignments, locallyHiddenGuids],
  );

  // ── Tray-context derived values ──

  // Add mode: the picked user. Edit mode: the user being edited.
  const focusUserGuid =
    trayCtx?.mode === "add"
      ? pickUserGuid
      : trayCtx?.mode === "edit"
        ? trayCtx.assignment.userGuid
        : "";

  const focusUser = focusUserGuid
    ? userByGuid.get(normalizeGuid(focusUserGuid)) ?? null
    : null;

  // Sender (acting user) — for the default templated message signature.
  const senderUser = currentUserGuid
    ? userByGuid.get(normalizeGuid(currentUserGuid)) ?? null
    : null;

  // 2FA cross-check. Only meaningful when the slot has a bundle attached.
  const bundleRequiresTwoFa = slotPermissionSet?.requires2fa === true;
  const userHasTwoFa = focusUser?.isTwoFactorEnabled === true;
  const grantWillBeDormant = !!focusUser && bundleRequiresTwoFa && !userHasTwoFa;
  const grantWillActivateImmediately =
    !!focusUser && bundleRequiresTwoFa && userHasTwoFa;

  // Impact signature for the focused user (radar pane in IdentityCard).
  const { tiers: focusUserTiers, loading: focusUserImpactLoading } =
    useApiImpactSignature(focusUserGuid || null);

  // ── Templated default invitation message ──

  const defaultInviteMessage = useMemo(
    () =>
      computeDefaultInviteMessage({
        recipient: focusUser,
        roleName: slot.roleLabel,
        projectName,
        sender: senderUser,
        t,
        templateKey: "assignment.messageTemplate",
        fallbackProjectNameKey: "assignment.thisProject",
      }),
    [focusUser, slot.roleLabel, projectName, senderUser, t],
  );

  // Live-default until the admin edits. Add mode only.
  useEffect(() => {
    if (messageUserEdited) return;
    if (trayCtx?.mode !== "add") return;
    setInviteMessage(defaultInviteMessage);
  }, [defaultInviteMessage, messageUserEdited, trayCtx?.mode]);

  // ── Tray lifecycle ──

  const openAddTray = useCallback(() => {
    setPickUserGuid("");
    setUserSearch("");
    setUserPickerOpen(false);
    setInviteMessage("");
    setMessageUserEdited(false);
    setConfirmingRemove(false);
    setRemoveReason("");
    setTrayCtx({ mode: "add" });
  }, []);

  const openEditTray = useCallback((assignment: ProjectRoleAssignmentResponse) => {
    setPickUserGuid("");
    setUserSearch("");
    setConfirmingRemove(false);
    setRemoveReason("");
    setTrayCtx({ mode: "edit", assignment });
  }, []);

  const closeTray = useCallback(() => {
    setTrayCtx(null);
    setConfirmingRemove(false);
    setRemoveReason("");
  }, []);

  const handleResetMessage = useCallback(() => {
    setInviteMessage(defaultInviteMessage);
    setMessageUserEdited(false);
  }, [defaultInviteMessage]);

  // ── Mutations ──

  const handleAssign = useCallback(() => {
    if (!pickUserGuid) return;
    onAssign(pickUserGuid);
    closeTray();
  }, [pickUserGuid, onAssign, closeTray]);

  const handleSendInvite = useCallback(() => {
    if (!pickUserGuid) return;
    onInvite(pickUserGuid, inviteMessage.trim() || undefined);
    closeTray();
  }, [pickUserGuid, inviteMessage, onInvite, closeTray]);

  const handleRemoveConfirm = useCallback(() => {
    if (trayCtx?.mode !== "edit") return;
    onRemove(
      trayCtx.assignment.assignmentGuid,
      removeReason.trim() || undefined,
    );
    closeTray();
  }, [trayCtx, removeReason, onRemove, closeTray]);

  const handleRevokeInvitation = useCallback(() => {
    if (trayCtx?.mode !== "edit") return;
    if (!onRevokeInvitation) return;
    onRevokeInvitation(trayCtx.assignment.assignmentGuid);
    closeTray();
  }, [trayCtx, onRevokeInvitation, closeTray]);

  // ── Row renderers ──

  const renderAvatar = (user: SecurityUserResponse | undefined) => {
    if (!user) {
      return (
        <div className={styles.avatar}>
          <span className={styles.avatarInitials}>??</span>
        </div>
      );
    }
    const img = profileImgUrl(user);
    return (
      <div className={styles.avatar}>
        {img ? (
          <img src={img} alt="" className={styles.avatarImg} />
        ) : (
          <span className={styles.avatarInitials}>
            {computeInitials(user.displayName || user.name || "")}
          </span>
        )}
      </div>
    );
  };

  const renderActiveRow = (a: ProjectRoleAssignmentResponse) => {
    const user = userByGuid.get(normalizeGuid(a.userGuid));
    return (
      <div key={a.assignmentGuid} className={styles.assignedRow}>
        {renderAvatar(user)}
        <div className={styles.userInfo}>
          <div className={styles.userName}>
            {user?.displayName || user?.name || a.userGuid}
          </div>
          {user?.screenName && (
            <div className={styles.userHandle}>@{user.screenName}</div>
          )}
        </div>
        {onUpdateVisibility && (
          <select
            className={styles.visibilitySelect}
            value={(a.visibility as MemberVisibility) ?? "team_only"}
            onChange={(e) =>
              onUpdateVisibility(
                a.assignmentGuid,
                e.target.value as MemberVisibility,
              )
            }
          >
            <option value="public">{t("assignment.visibilityPublic")}</option>
            <option value="team_only">{t("assignment.visibilityTeamOnly")}</option>
            <option value="anonymous">{t("assignment.visibilityAnonymous")}</option>
          </select>
        )}
        <button
          type="button"
          className={styles.btnRowDanger}
          onClick={() => openEditTray(a)}
        >
          {t("assignment.remove")}
        </button>
      </div>
    );
  };

  const renderPendingRow = (a: ProjectRoleAssignmentResponse) => {
    const user = userByGuid.get(normalizeGuid(a.userGuid));
    const recipientName = user?.displayName || user?.name || "this person";
    return (
      <div key={a.assignmentGuid} className={styles.assignedRow}>
        {renderAvatar(user)}
        <div className={styles.userInfo}>
          <div className={styles.userName}>
            {user?.displayName || user?.name || a.userGuid}
          </div>
          <div className={styles.userHandle}>
            {t("assignment.invitedOn", { date: shortDate(a.invitedAt) })}
          </div>
        </div>
        <span className={styles.invitedBadge}>{t("assignment.invited")}</span>
        {/* DEV-1039 iter 3 — Cycle 1 fix. Rescind is one-click direct
            with optimistic UI removal: hide the row immediately, then
            await the backend. If the backend rejects, surface the error
            and unhide. Guards against silent failures, stale cache, and
            slow refetch. */}
        <button
          type="button"
          className={styles.btnRowDanger}
          onClick={async () => {
            if (!onRevokeInvitation) return;
            const confirmText = t("assignment.revokeConfirmText", {
              name: recipientName,
            });
            if (!window.confirm(confirmText)) return;
            // Optimistically hide
            setLocallyHiddenGuids((prev) => {
              const next = new Set(prev);
              next.add(a.assignmentGuid);
              return next;
            });
            setActionError(null);
            try {
              await onRevokeInvitation(a.assignmentGuid);
            } catch (err: any) {
              // Backend rejected — unhide and surface
              setLocallyHiddenGuids((prev) => {
                const next = new Set(prev);
                next.delete(a.assignmentGuid);
                return next;
              });
              setActionError(
                err?.message
                  ? `Rescind failed: ${err.message}`
                  : "Rescind failed. Try again or reload.",
              );
            }
          }}
        >
          {t("assignment.revokeInvite")}
        </button>
      </div>
    );
  };

  // ── Body content ──

  const totalAssignments = activeAssignments.length + pendingInvitations.length;

  return (
    <>
      {ReactDOM.createPortal(
        <div
          className={styles.backdrop}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <div className={styles.modal}>
            {/* Header */}
            <div className={styles.header}>
              <span>
                <span className={styles.title}>{t("assignment.title")}</span>
                <span className={styles.roleName}>
                  {" "}
                  — {slot.roleLabel} ({slot.slotsFilled}/{slot.slotsNeeded})
                </span>
              </span>
              <CloseButton onClick={onClose} size="md" />
            </div>

            {/* Body */}
            <div className={styles.body}>
              {actionError && (
                <div className={styles.actionErrorBanner}>
                  <span>{actionError}</span>
                  <button
                    type="button"
                    className={styles.actionErrorDismiss}
                    onClick={() => setActionError(null)}
                  >
                    ×
                  </button>
                </div>
              )}
              {/* Section header with Add button */}
              <div className={styles.sectionHeader}>
                <div className={styles.sectionTitle}>
                  {t("assignment.peopleHeader")}
                  {totalAssignments > 0 && (
                    <span className={styles.count}>({totalAssignments})</span>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.btnAdd}
                  onClick={openAddTray}
                >
                  + {t("assignment.addPerson")}
                </button>
              </div>

              {/* 2FA hint at the slot level — admins see this BEFORE picking
                  a person, so they don't waste time on someone whose grant
                  will be dormant. */}
              {bundleRequiresTwoFa && (
                <div className={styles.slotInfo2fa}>
                  {t("assignment.slotRequires2fa", {
                    bundle: slotPermissionSet?.name ?? "",
                  })}
                </div>
              )}

              {totalAssignments === 0 ? (
                <div className={styles.emptyState}>
                  <div className={styles.emptyTitle}>
                    {t("assignment.emptyTitle")}
                  </div>
                  <div className={styles.emptyHelp}>
                    {t("assignment.emptyHelp")}
                  </div>
                </div>
              ) : (
                <>
                  {pendingInvitations.length > 0 && (
                    <div className={styles.assignedSection}>
                      <div className={styles.sectionLabel}>
                        {t("assignment.invited")} ({pendingInvitations.length})
                      </div>
                      {pendingInvitations.map(renderPendingRow)}
                    </div>
                  )}
                  {activeAssignments.length > 0 && (
                    <div className={styles.assignedSection}>
                      <div className={styles.sectionLabel}>
                        {t("assignment.assigned")} ({activeAssignments.length})
                      </div>
                      {activeAssignments.map(renderActiveRow)}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* ── Add / Edit tray ────────────────────────────────────── */}

      <Tray
        open={trayCtx !== null}
        onClose={closeTray}
        title={
          trayCtx?.mode === "add"
            ? t("assignment.trayAddTitle", { role: slot.roleLabel })
            : trayCtx?.mode === "edit"
              ? t("assignment.trayEditTitle", {
                  name:
                    userByGuid.get(normalizeGuid(trayCtx.assignment.userGuid))
                      ?.displayName ?? "this person",
                })
              : undefined
        }
        width={460}
      >
        <Tray.Body className={styles.trayBody}>
          {trayCtx?.mode === "add" && (
            <>
              {/* User picker / identity card */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>
                  {t("assignment.fieldUser")}
                </label>
                {focusUser ? (
                  <UserIdentityCard
                    user={focusUser}
                    homeCommunityName={resolveHomeCommunityName(
                      focusUser,
                      communityNameByGuid,
                    )}
                    tiers={focusUserTiers}
                    loading={focusUserImpactLoading}
                    showNo2faPill={grantWillBeDormant}
                    onClear={() => {
                      setPickUserGuid("");
                      setUserSearch("");
                    }}
                    pillNo2faKey="assignment.pillNo2fa"
                    pillNo2faTooltipKey="assignment.pillNo2faTooltip"
                    changePersonKey="assignment.changePerson"
                    homeCommunityLineKey="assignment.homeCommunityLine"
                    noHomeCommunityKey="assignment.noHomeCommunity"
                    impactLoadingKey="assignment.impactLoading"
                    impactUnavailableKey="assignment.impactUnavailable"
                    t={t}
                  />
                ) : (
                  <div className={styles.typeahead}>
                    <input
                      className={styles.input}
                      type="text"
                      value={userSearch}
                      onChange={(e) => {
                        setUserSearch(e.target.value);
                        setUserPickerOpen(true);
                      }}
                      onFocus={() => setUserPickerOpen(true)}
                      onBlur={() =>
                        setTimeout(() => setUserPickerOpen(false), 150)
                      }
                      placeholder={t("assignment.userSearchPlaceholder")}
                      autoFocus
                    />
                    {userPickerOpen && userSearch.trim() !== "" && (
                      <div className={styles.typeaheadResults}>
                        {userOptions.length === 0 ? (
                          <div className={styles.typeaheadEmpty}>
                            {t("assignment.noResults")}
                          </div>
                        ) : (
                          userOptions.map((u) => (
                            <div
                              key={u.userGuid}
                              className={styles.typeaheadItem}
                              onMouseDown={() => {
                                setPickUserGuid(u.userGuid);
                                setUserSearch("");
                                setUserPickerOpen(false);
                              }}
                            >
                              <div className={styles.typeaheadName}>
                                {u.displayName || u.name || "(no name)"}
                              </div>
                              {u.email && (
                                <div className={styles.typeaheadEmail}>
                                  {u.email}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 2FA cross-check warnings — only when a user is picked. */}
              {grantWillBeDormant && (
                <div className={styles.bundleWarn2fa}>
                  {t("assignment.dormantWarning", {
                    name: focusUser?.displayName || focusUser?.name || "",
                  })}
                </div>
              )}
              {grantWillActivateImmediately && (
                <div className={styles.bundleInfo2fa}>
                  {t("assignment.willActivateImmediately", {
                    name: focusUser?.displayName || focusUser?.name || "",
                  })}
                </div>
              )}

              {/* Templated invitation message — always rendered when a user
                  is picked. Only consumed by the "Send invitation" path;
                  the direct "Assign" path ignores it. */}
              {pickUserGuid && (
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>
                    {t("assignment.fieldMessage")}
                    <span className={styles.fieldSub}>
                      {t("assignment.fieldMessageSub")}
                    </span>
                  </label>
                  <textarea
                    className={styles.textarea}
                    value={inviteMessage}
                    onChange={(e) => {
                      setInviteMessage(e.target.value);
                      if (!messageUserEdited) setMessageUserEdited(true);
                    }}
                    placeholder={t("assignment.messagePlaceholder")}
                    rows={7}
                    maxLength={500}
                  />
                  <div className={styles.messageFooter}>
                    {messageUserEdited &&
                    inviteMessage !== defaultInviteMessage &&
                    defaultInviteMessage !== "" ? (
                      <button
                        type="button"
                        className={styles.btnResetMessage}
                        onClick={handleResetMessage}
                      >
                        {t("assignment.resetMessage")}
                      </button>
                    ) : (
                      <span />
                    )}
                    <div className={styles.charCount}>
                      {inviteMessage.length} / 500
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {trayCtx?.mode === "edit" && focusUser && (
            <>
              {/* Identity card — read-only here; no Change affordance. */}
              <div className={styles.field}>
                <UserIdentityCard
                  user={focusUser}
                  homeCommunityName={resolveHomeCommunityName(
                    focusUser,
                    communityNameByGuid,
                  )}
                  tiers={focusUserTiers}
                  loading={focusUserImpactLoading}
                  showNo2faPill={grantWillBeDormant}
                  pillNo2faKey="assignment.pillNo2fa"
                  pillNo2faTooltipKey="assignment.pillNo2faTooltip"
                  changePersonKey="assignment.changePerson"
                  homeCommunityLineKey="assignment.homeCommunityLine"
                  noHomeCommunityKey="assignment.noHomeCommunity"
                  impactLoadingKey="assignment.impactLoading"
                  impactUnavailableKey="assignment.impactUnavailable"
                  t={t}
                />
              </div>

              {/* Inline remove confirm (active) or revoke confirm (pending).
                  Replaces the old ExitReasonDialog with an in-tray flow. */}
              {confirmingRemove ? (
                trayCtx.assignment.status === "pending_invitation" ? (
                  <div className={styles.removeConfirm}>
                    <div className={styles.removeConfirmText}>
                      {t("assignment.revokeConfirmText", {
                        name:
                          focusUser.displayName || focusUser.name || "",
                      })}
                    </div>
                  </div>
                ) : (
                  <div className={styles.removeConfirm}>
                    <div className={styles.removeConfirmText}>
                      {t("assignment.removeConfirmText", {
                        name:
                          focusUser.displayName || focusUser.name || "",
                      })}
                    </div>
                    <label className={styles.fieldLabel}>
                      {t("assignment.removeReason")}
                    </label>
                    <textarea
                      className={styles.textarea}
                      value={removeReason}
                      onChange={(e) => setRemoveReason(e.target.value)}
                      placeholder={t("assignment.removeReasonPlaceholder")}
                      rows={3}
                      maxLength={500}
                    />
                  </div>
                )
              ) : null}
            </>
          )}
        </Tray.Body>

        <Tray.Footer className={styles.trayFooter}>
          {trayCtx?.mode === "add" && (
            <div className={styles.trayFooterEnd}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={closeTray}
              >
                {t("assignment.cancel")}
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleAssign}
                disabled={!pickUserGuid}
                title={t("assignment.submitAssignTooltip")}
              >
                {t("assignment.submitAssign")}
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleSendInvite}
                disabled={!pickUserGuid}
                title={t("assignment.submitInviteTooltip")}
              >
                {t("assignment.submitInvite")}
              </button>
            </div>
          )}

          {trayCtx?.mode === "edit" && (
            <>
              {!confirmingRemove ? (
                <>
                  <button
                    type="button"
                    className={styles.btnDanger}
                    onClick={() => setConfirmingRemove(true)}
                  >
                    {trayCtx.assignment.status === "pending_invitation"
                      ? t("assignment.revokeInvite")
                      : t("assignment.remove")}
                  </button>
                  <div className={styles.trayFooterEnd}>
                    <button
                      type="button"
                      className={styles.btnSecondary}
                      onClick={closeTray}
                    >
                      {t("assignment.close")}
                    </button>
                  </div>
                </>
              ) : (
                <div className={styles.trayFooterEnd}>
                  <button
                    type="button"
                    className={styles.btnSecondary}
                    onClick={() => {
                      setConfirmingRemove(false);
                      setRemoveReason("");
                    }}
                  >
                    {t("assignment.cancel")}
                  </button>
                  <button
                    type="button"
                    className={styles.btnDangerSolid}
                    onClick={
                      trayCtx.assignment.status === "pending_invitation"
                        ? handleRevokeInvitation
                        : handleRemoveConfirm
                    }
                    disabled={
                      trayCtx.assignment.status === "pending_invitation" &&
                      !onRevokeInvitation
                    }
                  >
                    {trayCtx.assignment.status === "pending_invitation"
                      ? t("assignment.confirmRevoke")
                      : t("assignment.confirmRemove")}
                  </button>
                </div>
              )}
            </>
          )}
        </Tray.Footer>
      </Tray>
    </>
  );
};

export default RoleAssignmentModal;
